import { useEffect, useRef, useState, useCallback } from "react";

interface UseIntersectionObserverProps {
  threshold?: number | number[];
  root?: Element | null;
  rootMargin?: string;
  freezeOnceVisible?: boolean;
}

export const useIntersectionObserver = ({
  threshold = 0,
  root = null,
  rootMargin = "0px",
  freezeOnceVisible = false,
}: UseIntersectionObserverProps = {}) => {
  const [entry, setEntry] = useState<IntersectionObserverEntry | null>(null);
  const [node, setNode] = useState<Element | null>(null);
  const observer = useRef<IntersectionObserver | null>(null);

  const frozen = entry?.isIntersecting && freezeOnceVisible;

  const updateEntry = useCallback(([entry]: IntersectionObserverEntry[]) => {
    setEntry(entry || null);
  }, []);

  useEffect(() => {
    const hasIOSupport = !!window.IntersectionObserver;

    if (!hasIOSupport || frozen || !node) return;

    const observerParams = { threshold, root, rootMargin };
    const currentObserver = new IntersectionObserver(
      updateEntry,
      observerParams,
    );

    observer.current = currentObserver;
    currentObserver.observe(node);

    return () => {
      currentObserver.disconnect();
    };
  }, [node, threshold, root, rootMargin, frozen, updateEntry]);

  const ref = useCallback((nodeElement: Element | null) => {
    setNode(nodeElement);
  }, []);

  return { ref, entry, isIntersecting: !!entry?.isIntersecting };
};

export const useLazyLoading = (options?: UseIntersectionObserverProps) => {
  const { ref, isIntersecting } = useIntersectionObserver({
    threshold: 0.1,
    ...options,
  });

  return {
    ref,
    isVisible: isIntersecting,
    shouldLoad: isIntersecting,
  };
};
